/**
 * InsightBarChart Component
 *
 * Displays insights by agent as a horizontal bar chart.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Users } from 'lucide-react';

// Distinct colors for each agent bar
const COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];

// Custom Y-axis tick with agent avatar + name
const CustomYAxisTick = ({ x, y, payload, chartData }) => {
  const agent = chartData?.find(d => d.agentName === payload.value);
  const avatarUrl = agent?.avatarUrl;
  const name = payload.value || '';
  const initial = name.charAt(0).toUpperCase();
  const agentIndex = chartData?.findIndex(d => d.agentName === payload.value) ?? 0;
  const color = COLORS[agentIndex % COLORS.length];
  const avatarSize = 24;
  const avatarR = avatarSize / 2;

  return (
    <g transform={`translate(${x},${y})`}>
      <defs>
        <clipPath id={`avatar-clip-${agentIndex}`}>
          <circle cx={-avatarR - 4} cy={0} r={avatarR} />
        </clipPath>
      </defs>
      {avatarUrl ? (
        <image
          href={avatarUrl}
          x={-avatarSize - 4}
          y={-avatarR}
          width={avatarSize}
          height={avatarSize}
          clipPath={`url(#avatar-clip-${agentIndex})`}
          preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <>
          <circle cx={-avatarR - 4} cy={0} r={avatarR} fill={color} opacity={0.3} />
          <text
            x={-avatarR - 4}
            y={1}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={color}
            fontSize={11}
            fontWeight="bold"
          >
            {initial}
          </text>
        </>
      )}
      <text
        x={-avatarSize - 12}
        y={1}
        textAnchor="end"
        dominantBaseline="middle"
        fill="#9ca3af"
        fontSize={12}
      >
        {name.length > 14 ? name.slice(0, 14) + '…' : name}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-sm font-medium text-white">
        {payload[0].payload.agentName}
      </p>
      <p className="text-xs text-gray-400">
        {payload[0].value} Insights
      </p>
    </div>
  );
};

export default function InsightBarChart({ data = [], isLoading = false }) {
  if (isLoading) {
    return (
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-purple-400" />
          <h3 className="text-sm font-medium text-white">Insights by Agent</h3>
        </div>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-pulse text-gray-500">Lade Daten...</div>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-purple-400" />
          <h3 className="text-sm font-medium text-white">Insights by Agent</h3>
        </div>
        <div className="h-64 flex items-center justify-center text-gray-500">
          Keine Daten vorhanden
        </div>
      </div>
    );
  }

  // Take top 6 agents
  const chartData = data.slice(0, 6);

  return (
    <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-purple-400" />
        <h3 className="text-sm font-medium text-white">Insights by Agent</h3>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
            <XAxis
              type="number"
              stroke="#9ca3af"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="agentName"
              tickLine={false}
              axisLine={false}
              width={150}
              tick={<CustomYAxisTick chartData={chartData} />}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
